import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react'

import { buildHeaders } from 'helpers/api'

import { Constants, ResponseEntitiesList, ResponseEntityId } from 'types'
import { IUser } from 'types/models/user.model'
import { INotificationEvent } from 'types/notification-event.model'
import {
	ChangeNotificationSettings,
	ChangeNotificationStatusPayload,
	CoursesParticipationInfo,
	GetCourseNotificationEventsPayload,
	GetNotificationEventsPayload,
	GetNotificationSettings,
	GetNotificationSettingsResponse,
	GetNotificationsPayload,
	GetNotificationsResponse,
	GetRoleInCourse,
	GetUsersParticipationInCourse,
	RoleInCourseResponse,
	UserQueryApiRoutes,
	UsersListFilter
} from './types'

const userQuery = createApi({
	reducerPath: 'userQuery ',
	tagTypes: ['user'],
	baseQuery: fetchBaseQuery({
		baseUrl: `${Constants.API_BASE_URL}/${UserQueryApiRoutes.BASE_URL}`
	}),
	endpoints: (build) => ({
		getUserRole: build.query<RoleInCourseResponse, GetRoleInCourse>({
			query: ({ userId }) => ({
				url: `${userId}/role-in-course`,
				method: 'GET',
				headers: buildHeaders({ needAuth: true })
			}),
			providesTags: ['user']
		}),
		getYourself: build.query<{ data: IUser }, string | void>({
			query: (token) => ({
				url: '',
				method: 'GET',
				headers: buildHeaders({
					needAuth: true,
					headers: token ? { Authorization: `Bearer ${token}` } : {}
				})
			}),
			providesTags: ['user']
		}),
		getUserById: build.query<{ data: IUser }, ResponseEntityId>({
			query: (userId) => ({
				url: `/${userId}`,
				method: 'GET',
				headers: buildHeaders({ needAuth: true })
			}),
			providesTags: ['user']
		}),
		getUsers: build.query<ResponseEntitiesList<IUser>, UsersListFilter>({
			query: (filter) => ({
				url: '',
				method: 'POST',
				body: filter,
				headers: buildHeaders({ needAuth: true }, filter)
			}),
			providesTags: ['user']
		}),
		getUsersParticipationInCourse: build.query<
			CoursesParticipationInfo,
			GetUsersParticipationInCourse
		>({
			query: ({ userId, schoolId }) => ({
				url: `${userId}/participation-in-courses/school/${schoolId}`,
				method: 'GET',
				headers: buildHeaders({ needAuth: true })
			}),
			providesTags: ['user']
		}),
		updateYourself: build.mutation<undefined, FormData>({
			query: (body) => ({
				url: UserQueryApiRoutes.UPDATE_YOURSELF,
				method: 'POST',
				body,
				headers: buildHeaders({ needAuth: true })
			}),
			invalidatesTags: ['user']
		}),
		getEventsForNotifications: build.mutation<
			INotificationEvent[],
			GetNotificationEventsPayload | GetCourseNotificationEventsPayload
		>({
			query: (body) => ({
				url: UserQueryApiRoutes.GET_EVENTS_FOR_NOTIFICATIONS,
				method: 'POST',
				body,
				headers: buildHeaders({ needAuth: true })
			})
		}),
		getNotifications: build.query<
			GetNotificationsResponse,
			GetNotificationsPayload
		>({
			query: (body) => ({
				url: UserQueryApiRoutes.GET_NOTIFICATIONS,
				method: 'GET',
				params: body,
				headers: buildHeaders({ needAuth: true })
			})
		}),
		getNotificationSettings: build.mutation<
			GetNotificationSettingsResponse,
			GetNotificationSettings | GetCourseNotificationEventsPayload
		>({
			query: (body) => ({
				url: UserQueryApiRoutes.GET_NOTIFICATION_SETTINGS,
				method: 'POST',
				body,
				headers: buildHeaders({ needAuth: true })
			})
		}),
		changeNotificationSettings: build.mutation<
			void,
			ChangeNotificationSettings
		>({
			query: (body) => ({
				url: UserQueryApiRoutes.CHANGE_NOTIFICATION_SETTINGS,
				method: 'POST',
				body,
				headers: buildHeaders({ needAuth: true })
			})
		}),
		changeNotificationStatus: build.mutation<
			void,
			ChangeNotificationStatusPayload
		>({
			query: (body) => ({
				url: UserQueryApiRoutes.CHANGE_NOTIFICATION_STATUS,
				method: 'POST',
				body,
				headers: buildHeaders({ needAuth: true })
			})
		})
	})
})

export default userQuery
